import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { X } from 'lucide-react';
import { FilteredBoardExport } from './FilteredBoardExport';

/**
 * Sticky breadcrumb of all currently-active filters on the planning week
 * board. Each chip carries its own "X" to remove just that one; "Tümünü
 * temizle" wipes the whole row. Hidden when nothing is active.
 *
 * Filter categories shown (color-coded so operators can scan quickly):
 *   - Board firmaları    (indigo)  — top toolbar context
 *   - Çalışan firmaları  (indigo)  — left panel multi-select
 *   - Müşteriler         (emerald) — customer (PostNL/DPD/...)
 *   - Depolar            (teal)    — depot subfilter
 *   - Rotalar            (amber)   — route subfilter
 *   - Çalışan durumu     (blue)    — single-select all/available/assigned/leave
 */
export function ActiveFiltersBreadcrumb({
  activeFilterCount,
  board,
  year, week,
  routeGroups, assignByCell, visibleDates,
  filterFirmIds, setFilterFirmIds,
  empFirmIds, setEmpFirmIds, setIncludeBorrowed,
  filterCompanyIds, setFilterCompanyIds,
  filterDepotIds, setFilterDepotIds,
  filterRouteIds, setFilterRouteIds,
  empFilter, setEmpFilter,
  clearAllFilters,
}) {
  // Aktif filtre olmasa bile bar'ı göster — kullanıcının export butonları
  // her zaman erişilebilir olsun. Filtre yoksa "Tüm görünüm" rozeti
  // gösterilir, board olduğu gibi (filtresiz) export edilir.
  return (
    <Card className="px-3 py-1.5 flex flex-wrap items-center gap-2 text-[11px]" data-testid="planning-active-filters-bar">
      <span className="text-slate-500 font-semibold">Aktif filtreler:</span>
      {!activeFilterCount && (
        <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500 italic">
          Filtre yok — tam görünüm
        </Badge>
      )}
      {[...filterFirmIds].map((id) => {
        const f = (board?.firms || []).find((x) => x.id === id);
        return (
          <Badge key={id} variant="outline" className="text-[10px] border-indigo-300 text-indigo-700" data-testid={`planning-active-board-firm-${id}`}>
            Board: {f?.name || '—'}
            <button onClick={() => setFilterFirmIds((prev) => {
              const next = new Set(prev); next.delete(id); return next;
            })} className="ml-1 hover:text-red-500"><X className="h-3 w-3" /></button>
          </Badge>
        );
      })}
      {[...empFirmIds].map((id) => {
        const f = (board?.firms || []).find((x) => x.id === id);
        return (
          <Badge key={id} variant="outline" className="text-[10px] border-indigo-300 text-indigo-700" data-testid={`planning-active-emp-firm-${id}`}>
            Çalışan: {f?.name || '—'}
            <button onClick={() => setEmpFirmIds((prev) => {
              const next = new Set(prev); next.delete(id);
              if (next.size === 0) setIncludeBorrowed(false);
              return next;
            })} className="ml-1 hover:text-red-500"><X className="h-3 w-3" /></button>
          </Badge>
        );
      })}
      {[...filterCompanyIds].map((id) => {
        const c = (board?.companies || []).find((x) => x.id === id);
        return (
          <Badge key={id} variant="outline" className="text-[10px] border-emerald-300 text-emerald-700" data-testid={`planning-active-company-${id}`}>
            Müşteri: {c?.name || '—'}
            <button onClick={() => {
              const n = new Set(filterCompanyIds); n.delete(id); setFilterCompanyIds(n);
            }} className="ml-1 hover:text-red-500"><X className="h-3 w-3" /></button>
          </Badge>
        );
      })}
      {[...filterDepotIds].map((id) => {
        const d = (board?.depots || []).find((x) => x.id === id);
        return (
          <Badge key={id} variant="outline" className="text-[10px] border-teal-300 text-teal-700" data-testid={`planning-active-depot-${id}`}>
            Depo: {d?.name || '—'}
            <button onClick={() => {
              const n = new Set(filterDepotIds); n.delete(id); setFilterDepotIds(n);
            }} className="ml-1 hover:text-red-500"><X className="h-3 w-3" /></button>
          </Badge>
        );
      })}
      {[...filterRouteIds].map((id) => {
        const r = (board?.routes || []).find((x) => x.id === id);
        return (
          <Badge key={id} variant="outline" className="text-[10px] border-amber-300 text-amber-700" data-testid={`planning-active-route-${id}`}>
            Rota: {r?.route_code || '—'}
            <button onClick={() => {
              const n = new Set(filterRouteIds); n.delete(id); setFilterRouteIds(n);
            }} className="ml-1 hover:text-red-500"><X className="h-3 w-3" /></button>
          </Badge>
        );
      })}
      {empFilter !== 'all' && (
        <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700">
          Durum: {empFilter === 'available' ? 'Müsait' : empFilter === 'assigned' ? 'Atanmış' : 'İzinli'}
          <button onClick={() => setEmpFilter('all')} className="ml-1 hover:text-red-500"><X className="h-3 w-3" /></button>
        </Badge>
      )}
      <button onClick={clearAllFilters} className={`ml-auto text-[10px] text-slate-500 hover:text-slate-700 underline ${!activeFilterCount ? 'invisible' : ''}`} data-testid="planning-active-clear-all">
        Tümünü temizle
      </button>
      <FilteredBoardExport
        year={year}
        week={week}
        board={board}
        routeGroups={routeGroups}
        assignByCell={assignByCell}
        visibleDates={visibleDates}
      />
    </Card>
  );
}
